/**
 * AppShell のナビ定義（UI 刷新 Phase 2a）。
 *
 * 管理者と super_admin の全ページを 1 本ずつの宣言的な配列で持つ
 * （docs/UI_REDESIGN.md「ロール別ナビと URL」）。サイドバー・スマホの下タブ・
 * マスタ管理ページのタブ・ルート → タブ id の逆引きは全部ここから引く。
 * キャスト / ドライバー（Phase 2b、PhoneShell）の 3 タブも末尾にある。
 *
 * 表示可否は旧 AdminDashboard の `allAdminTabs.filter(...)` と同じ規則を
 * エントリ側に寄せたもの:
 *   - hiddenForShopAdmin        → shop_admin / admin を roles から外す
 *   - corporateAdminOnly        → roles = [corporate_admin]
 *   - hiddenForCorporateAdmin   → corporate_admin を roles から外す
 *   - requiresVehicleManagement → canManageVehicles() と同じ [corporate_admin, super_admin]
 *   - requiresStandard          → requiresFeature（hasFeatureAccess に渡す id）
 * 同値であることは __tests__/navItems.test.ts が旧ロジックと突き合わせて保証する。
 */
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  Ban,
  Bell,
  Building2,
  Car,
  Clock,
  Coins,
  Database,
  History,
  Home,
  LayoutDashboard,
  Mail,
  MapPin,
  Megaphone,
  Receipt,
  Route,
  ScrollText,
  Settings,
  ShieldCheck,
  Store,
  UserRound,
  Users,
  UsersRound,
  ChartColumn,
  Navigation,
} from 'lucide-react';
import { ROLES } from '../../constants/roles';
import { hasFeatureAccess } from '../../constants/plans';

export type NavGroupKey = 'ops' | 'master' | 'reports' | 'settings';

export interface NavGroup {
  key: NavGroupKey;
  label: string;
  icon: LucideIcon;
  /**
   * グループ内のページ切り替えをページ自身が Tabs で持つ（マスタ管理）。
   * true のグループでは AppShell のスマホ用グループタブを出さない。
   */
  pageOwnsTabs?: boolean;
}

/** 下タブの並び順もこの順 */
export const NAV_GROUPS: readonly NavGroup[] = [
  { key: 'ops', label: '運行', icon: Navigation },
  { key: 'master', label: 'マスタ', icon: Database, pageOwnsTabs: true },
  { key: 'reports', label: 'レポート', icon: ChartColumn },
  { key: 'settings', label: '設定', icon: Settings },
];

export interface NavEntry {
  /** 旧タブ id。useAdminDashboard の visibleSlicesFor / SystemDashboard の effect が見る */
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
  group: NavGroupKey;
  /** このロールにだけ出す */
  roles: readonly string[];
  /** hasFeatureAccess(plan, requiresFeature) が真のときだけ出す（旧 requiresStandard） */
  requiresFeature?: string;
}

export interface NavContext {
  role: string;
  /** shop.billing_plan。未取得なら旧画面と同じく 'light' 扱いにする */
  billingPlan?: string | null;
}

const { ADMIN, SHOP_ADMIN, CORPORATE_ADMIN, SUPER_ADMIN } = ROLES;

/** 旧 filter に何の条件も無いタブ（管理者ダッシュボードを開ける全ロール） */
const EVERY_ADMIN = [ADMIN, SHOP_ADMIN, CORPORATE_ADMIN, SUPER_ADMIN] as const;
/** 旧 hiddenForShopAdmin（shop_admin / admin を外したもの） */
const NOT_SHOP_ADMIN = [CORPORATE_ADMIN, SUPER_ADMIN] as const;
/** 旧 requiresVehicleManagement = canManageVehicles() */
const VEHICLE_MANAGERS = [CORPORATE_ADMIN, SUPER_ADMIN] as const;
/** 旧 hiddenForCorporateAdmin */
const NOT_CORPORATE_ADMIN = [ADMIN, SHOP_ADMIN, SUPER_ADMIN] as const;
/** 旧 corporateAdminOnly */
const CORPORATE_ONLY = [CORPORATE_ADMIN] as const;

export const ADMIN_HOME = '/admin';
export const SYSTEM_HOME = '/system';

export const ADMIN_NAV: readonly NavEntry[] = [
  { id: 'dashboard', label: 'ダッシュボード', icon: LayoutDashboard, path: '/admin', group: 'ops', roles: EVERY_ADMIN },
  { id: 'cast-status', label: 'キャスト状況', icon: Activity, path: '/admin/cast-status', group: 'ops', roles: CORPORATE_ONLY },
  { id: 'proxy-request', label: '代理帰宅申請', icon: Home, path: '/admin/proxy-request', group: 'ops', roles: EVERY_ADMIN },
  { id: 'casts', label: 'キャスト管理', icon: Users, path: '/admin/master/casts', group: 'master', roles: EVERY_ADMIN },
  { id: 'drivers', label: 'ドライバー管理', icon: UserRound, path: '/admin/master/drivers', group: 'master', roles: VEHICLE_MANAGERS },
  { id: 'vehicles', label: '車両管理', icon: Car, path: '/admin/master/vehicles', group: 'master', roles: VEHICLE_MANAGERS },
  { id: 'ng', label: 'NG管理', icon: Ban, path: '/admin/master/ng', group: 'master', roles: EVERY_ADMIN, requiresFeature: 'ng' },
  { id: 'commute-groups', label: '帰宅グループ管理', icon: UsersRound, path: '/admin/master/groups', group: 'master', roles: NOT_SHOP_ADMIN, requiresFeature: 'commute-groups' },
  { id: 'waiting-location', label: '待機場所', icon: MapPin, path: '/admin/master/waiting', group: 'master', roles: VEHICLE_MANAGERS },
  { id: 'usage-history', label: '利用履歴', icon: History, path: '/admin/reports/usage', group: 'reports', roles: NOT_CORPORATE_ADMIN, requiresFeature: 'usage-history' },
  { id: 'time-restriction', label: '帰宅申請時間', icon: Clock, path: '/admin/settings/time', group: 'settings', roles: NOT_SHOP_ADMIN },
  { id: 'usage-fee', label: '利用料金', icon: Coins, path: '/admin/settings/fee', group: 'settings', roles: NOT_SHOP_ADMIN, requiresFeature: 'usage-fee' },
  { id: 'notifications', label: '通知', icon: Bell, path: '/admin/settings/notifications', group: 'settings', roles: EVERY_ADMIN },
];

const SYSTEM_ONLY = [SUPER_ADMIN] as const;

export const SYSTEM_NAV: readonly NavEntry[] = [
  { id: 'overview', label: '概要', icon: LayoutDashboard, path: '/system', group: 'ops', roles: SYSTEM_ONLY },
  { id: 'announcements', label: 'お知らせ', icon: Megaphone, path: '/system/announcements', group: 'ops', roles: SYSTEM_ONLY },
  { id: 'inquiries', label: 'お問い合わせ', icon: Mail, path: '/system/inquiries', group: 'ops', roles: SYSTEM_ONLY },
  { id: 'groups', label: 'グループ管理', icon: Building2, path: '/system/master/groups', group: 'master', roles: SYSTEM_ONLY },
  { id: 'shops', label: '店舗管理', icon: Store, path: '/system/master/shops', group: 'master', roles: SYSTEM_ONLY },
  { id: 'admins', label: '管理者アカウント管理', icon: ShieldCheck, path: '/system/master/admins', group: 'master', roles: SYSTEM_ONLY },
  { id: 'billing', label: '利用レポート', icon: Receipt, path: '/system/reports/billing', group: 'reports', roles: SYSTEM_ONLY },
  { id: 'monitoring', label: 'システム監視', icon: Activity, path: '/system/monitoring', group: 'reports', roles: SYSTEM_ONLY },
  { id: 'audit', label: '操作履歴', icon: ScrollText, path: '/system/audit', group: 'reports', roles: SYSTEM_ONLY },
];

/**
 * キャスト / ドライバーの下タブ（PhoneShell）。ロールで出し分ける項目は無く、
 * タブ集合は固定（docs/UI_REDESIGN.md）。id は旧 CastDashboard / DriverDashboard の
 * activeTab の値をそのまま使う。
 */
export interface PhoneNavEntry {
  /** 旧タブ id */
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
}

export const CAST_HOME = '/cast/request';
export const DRIVER_HOME = '/driver/shift';

export const CAST_NAV: readonly PhoneNavEntry[] = [
  { id: 'request', label: '帰宅申請', icon: Home, path: CAST_HOME },
  { id: 'address', label: '住所', icon: MapPin, path: '/cast/addresses' },
  { id: 'notifications', label: '通知', icon: Bell, path: '/cast/notifications' },
];

export const DRIVER_NAV: readonly PhoneNavEntry[] = [
  { id: 'attendance', label: '勤務', icon: Clock, path: DRIVER_HOME },
  { id: 'route', label: 'ルート', icon: Route, path: '/driver/route' },
  { id: 'notifications', label: '通知', icon: Bell, path: '/driver/notifications' },
];

/** 旧 filter と同じ判定。requiresFeature は旧 requiresStandard と同じく plan 未取得なら 'light' */
export function isNavEntryVisible(entry: NavEntry, ctx: NavContext): boolean {
  if (!entry.roles.includes(ctx.role)) return false;
  if (entry.requiresFeature && !hasFeatureAccess(ctx.billingPlan || 'light', entry.requiresFeature)) {
    return false;
  }
  return true;
}

export function visibleNavEntries(entries: readonly NavEntry[], ctx: NavContext): NavEntry[] {
  return entries.filter((entry) => isNavEntryVisible(entry, ctx));
}

/** 現在の URL に対応するエントリ。末尾スラッシュは無視する。無ければ undefined */
export function navEntryForPath<T extends { path: string }>(entries: readonly T[], pathname: string): T | undefined {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return entries.find((entry) => entry.path === normalized);
}

/** グループに属する（表示中の）エントリ。空配列のグループはナビに出さない */
export function entriesInGroup(entries: readonly NavEntry[], group: NavGroupKey): NavEntry[] {
  return entries.filter((entry) => entry.group === group);
}

/** 下タブでグループを押したときに開くページ = そのグループの先頭 */
export function firstEntryInGroup(entries: readonly NavEntry[], group: NavGroupKey): NavEntry | undefined {
  return entries.find((entry) => entry.group === group);
}

/** 旧 /dashboard。ホームが決まらないロールだけがここに留まる */
export const LEGACY_DASHBOARD = '/dashboard';

/** ロールの「ホーム」。旧 /dashboard と / のリダイレクト先 */
export function homePathForRole(role: string | null | undefined): string {
  if (role === SUPER_ADMIN) return SYSTEM_HOME;
  if (role === ADMIN || role === SHOP_ADMIN || role === CORPORATE_ADMIN) return ADMIN_HOME;
  if (role === ROLES.CAST) return CAST_HOME;
  if (role === ROLES.DRIVER) return DRIVER_HOME;
  return LEGACY_DASHBOARD;
}

/** Header.js の getRoleDisplay と同じ表記（ROLE_LABELS とは別物なので注意） */
export function roleDisplayName(role: string): string {
  const names: Record<string, string> = {
    [ROLES.CAST]: 'キャスト',
    [ROLES.DRIVER]: 'ドライバー',
    [ROLES.SHOP_ADMIN]: '店舗管理者',
    [ROLES.ADMIN]: '店舗管理者',
    [ROLES.CORPORATE_ADMIN]: '系列店管理者',
    [ROLES.SUPER_ADMIN]: 'システム管理者',
  };
  return names[role] || role;
}
